import React from "react";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    Box, Button, Card, CardContent, Chip, Grid, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography
} from "@mui/material";
import {Campaign, EmojiEvents} from "@mui/icons-material";
import CircularLoading from "../../../components/CircularLoading";
import {useGetSettings, useGetVendorWallet} from "../../../services/ContentManager";
import {ARTICLE_LIST_ROUTE, SPONSORED_NOTIFICATIONS_ROUTE} from "../../../constants/vendors";

// Allineato a DEFAULT_COST_PER_READ di SponsoredNotificationService: qui serve solo a
// mostrare il prezzo accanto al bottone.
const COST_PER_READ = 20;

const MOVEMENT_LABELS = {
    SALE: ['movementSale', 'Vendita'],
    ARTICLE_SPONSORSHIP: ['movementArticleSponsorship', 'Sponsorizzazione premio'],
    NOTIFICATION_READ: ['movementNotificationRead', 'Notifica letta']
};

export default function Wallet({vendor}) {
    const {t} = useTranslation();
    const history = useHistory();
    const uid = vendor?.uid;
    const {wallet, status} = useGetVendorWallet(uid);
    const {settings} = useGetSettings();

    const visualizationCost = settings?.find(s => s.name === 'advertisement_visualization_cost')?.value ?? '-';

    if (status === 'loading') return <CircularLoading/>;

    const balance = wallet?.balance != null ? parseFloat(wallet.balance) : 0;
    const movements = wallet?.movements || [];

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">{t('wallet', 'Portafoglio')}</Typography>
                <Chip label={`${t('balance')}: ${balance} ${t('points')}`} color="primary" variant="outlined"/>
            </Box>

            <Typography color="textSecondary" mb={2}>
                {t('walletExplain', 'I punti si guadagnano quando un pinbiker riscatta in negozio un premio comprato con i punti community. Si spendono per dare visibilità ai tuoi premi o per inviare notifiche promozionali.')}
            </Typography>

            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={6}>
                    <Card><CardContent>
                        <Typography variant="h6" gutterBottom>
                            <EmojiEvents sx={{verticalAlign: 'middle', mr: 1}}/>
                            {t('advertisedArticles')}
                        </Typography>
                        <Typography color="textSecondary" gutterBottom>
                            {visualizationCost} {t('points')} {t('perVisualization', 'per visualizzazione')}
                        </Typography>
                        <Button variant="contained" onClick={() => history.push(ARTICLE_LIST_ROUTE)}>
                            {t('advertisedArticles')}
                        </Button>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card><CardContent>
                        <Typography variant="h6" gutterBottom>
                            <Campaign sx={{verticalAlign: 'middle', mr: 1}}/>
                            {t('sponsoredNotifications')}
                        </Typography>
                        <Typography color="textSecondary" gutterBottom>
                            {COST_PER_READ} {t('points')} {t('perRead', 'per lettura')}
                        </Typography>
                        <Button variant="contained" onClick={() => history.push(SPONSORED_NOTIFICATIONS_ROUTE)}>
                            {t('sponsoredNotifications')}
                        </Button>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Typography variant="h6" mb={1}>{t('history')}</Typography>
            {movements.length === 0 ? (
                <Typography color="textSecondary">
                    {t('walletNoMovements', 'Nessun movimento registrato.')}
                </Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('date', 'Data')}</TableCell>
                                <TableCell>{t('type')}</TableCell>
                                <TableCell>{t('description')}</TableCell>
                                <TableCell align="right">{t('value')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {movements.map((m, i) => {
                                const [key, fallback] = MOVEMENT_LABELS[m.type] || [null, m.type];
                                const amount = parseFloat(m.amount);
                                return (
                                    <TableRow key={i}>
                                        <TableCell>{m.date ? new Date(m.date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>{key ? t(key, fallback) : fallback}</TableCell>
                                        <TableCell>{m.description || '-'}</TableCell>
                                        <TableCell align="right" sx={{color: amount < 0 ? 'error.main' : 'success.main'}}>
                                            {amount > 0 ? `+${amount}` : amount} {t('points')}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
